import React, { useState, useEffect, useRef } from "react";

const EMICalculator = ({ onClose }) => {
  const [loanAmount, setLoanAmount] = useState(100000);
  const [interestRate, setInterestRate] = useState(2);
  const [loanTenure, setLoanTenure] = useState(1);
  const [isVisible, setIsVisible] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    if (loanAmount && interestRate && loanTenure) {
      setIsVisible(true);
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [loanAmount, interestRate, loanTenure, onClose]);

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const monthlyInterestRate = interestRate / 12 / 100;
  const numberOfPayments = loanTenure * 12;
  const emi =
    (loanAmount *
      monthlyInterestRate *
      Math.pow(1 + monthlyInterestRate, numberOfPayments)) /
    (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);

  const totalAmount = emi * numberOfPayments;
  const totalInterest = totalAmount - loanAmount;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      style={{ fontFamily: "Barlow Condensed" }}
    >
      <div
        className={`relative bg-white p-6 md:p-24 transition-all duration-500 ease-in-out transform ${
          isVisible ? "opacity-100 scale-100" : "opacity-0 scale-90"
        }`}
        ref={modalRef}
      >
        <button
          onClick={onClose}
          className="absolute top-[-30px] right-2 mt-9 bg-red-500 text-white px-2 py-1 rounded"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-5 h-5"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L12 10.586l6.293-6.293a1 1 0 111.414 1.414L13.414 12l6.293 6.293a1 1 0 01-1.414 1.414L12 13.414l-6.293 6.293a1 1 0 01-1.414-1.414L10.586 12 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        <h1
          className="text-2xl md:text-3xl text-center mb-6 md:mb-8"
          style={{ fontFamily: "Ballega" }}
        >
          EMI Calculator
        </h1>
        <div
          className="bg-emerald-600 text-white p-4 md:p-6 shadow-lg rounded-lg"
          style={{ width: "30rem", maxWidth: "90vw", minWidth: "18rem" }}
        >
          <div className="mb-4">
            <span className="flex justify-between mb-2 text-xs md:text-sm">
              <label className="block font-medium">Loan Amount</label>
              <p>₹ {loanAmount.toLocaleString()}</p>
            </span>
            <input
              type="range"
              min="100000"
              max="100000000"
              step="1000"
              value={loanAmount}
              onChange={(e) => setLoanAmount(Number(e.target.value))}
              className="w-full"
            />
          </div>
          <div className="mb-4">
            <span className="flex justify-between mb-2 text-xs md:text-sm">
              <label className="block font-medium">
                Rate of Interest (P.A)
              </label>
              <p>{interestRate}%</p>
            </span>
            <input
              type="range"
              min="1"
              max="15"
              step="0.1"
              value={interestRate}
              onChange={(e) => setInterestRate(Number(e.target.value))}
              className="w-full"
            />
          </div>
          <div className="mb-4">
            <span className="flex justify-between mb-2 text-xs md:text-sm">
              <label className="block font-medium">Loan Tenure</label>
              <p>{loanTenure} Year</p>
            </span>
            <input
              type="range"
              min="1"
              max="30"
              step="1"
              value={loanTenure}
              onChange={(e) => setLoanTenure(Number(e.target.value))}
              className="w-full"
            />
          </div>
          <div className="flex flex-col justify-between rounded-lg mt-6 md:mt-9 text-xs md:text-sm">
            <div className="flex justify-between mb-2">
              <span>Monthly EMI Amount:</span>
              <span>₹{emi.toFixed(2)}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span>Principal Amount:</span>
              <span>₹{loanAmount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span>Total Interest:</span>
              <span>₹{totalInterest.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Total Amount:</span>
              <span>₹{totalAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EMICalculator;
